import { useState, useEffect } from 'react';
import { Crown, Zap, Star, RefreshCw, Edit3, XCircle, Loader2, Icon as LucideIcon, Unlink } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import {// Still needed for the modal
  useUpdateSubscriptionChannel,
  useUnlinkSubscriptionChannel,
  // useRenewSubscription, // Removed
  usePlans,
  Subscription,
  updateSubscriptionAutoRenewal,
  useStripeEnabled,
  useManualRenewalMessages,
} from '../../services/subscriptionService';
import ChangeChannelModal from './ChangeChannelModal'; // Import the modal
import { NoDefaultCardModal } from './NoDefaultCardModal';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

// Icon Mapping (Keep as it's relevant for displaying plan icon)
const iconMap: { [key: string]: LucideIcon } = {
  default: Zap,
  zap: Zap,
  star: Star,
  crown: Crown,
};

const getPlanIcon = (iconName: string | null): LucideIcon => {
  if (iconName && iconMap[iconName.toLowerCase()]) {
    return iconMap[iconName.toLowerCase()];
  }
  return iconMap.default;
};

interface SubscriptionManagementProps {
  activeSubscription: Subscription; // Expect a specific subscription to manage
  onSubscriptionUpdate: () => void; // Callback to inform parent of an update (e.g., to refetch list)
  onBackToList?: () => void; // Optional: Callback to go back to a list view
}

export default function SubscriptionManagement({ activeSubscription, onSubscriptionUpdate, onBackToList }: SubscriptionManagementProps) {
  const { t, i18n } = useTranslation();
  // console.log({autoRenewal: activeSubscription.autoRenewal})
  // console.log({endDate: activeSubscription.endDate})
  // console.log({DategetDate: new Date(new Date().setDate(new Date().getDate() + 5))})
  // console.log({Date: new Date()})
const now = new Date();
const endDate = new Date(activeSubscription.endDate);
const daysLeft = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

const shouldShowRenewButton = !activeSubscription.autoRenewal && daysLeft <= 5;
console.log({now: new Date(now.getTime() + 60 * 60 * 1000)})



  const navigate = useNavigate(); // Initialize useNavigate
  const queryClient = useQueryClient();

  // Data fetching for CHOICES or auxiliary data, not the primary activeSubscription
  const { data: plansData, isLoading: isLoadingPlans, error: plansError } = usePlans();
  const { data: stripeEnabledData } = useStripeEnabled();
  const stripeEnabled = stripeEnabledData ?? true;
  const { data: manualRenewalMessages } = useManualRenewalMessages();
  const manualRenewalMessage =
    manualRenewalMessages?.find((m) => m.language === i18n.language)?.message ??
    manualRenewalMessages?.find((m) => m.language === 'en')?.message;
  // UserChannels is fetched by ChangeChannelModal itself, so not strictly needed here unless for other purposes.
  // For simplicity, let modal handle its own channel list fetching.

  const [isChangeChannelModalOpen, setIsChangeChannelModalOpen] = useState(false);
  const [showNoDefaultCardModal, setShowNoDefaultCardModal] = useState(false);
  const [hasUnlinkedChannel, setHasUnlinkedChannel] = useState(false);

  // Mutations still use queryClient and will trigger necessary invalidations
  const { mutate: updateChannel, isLoading: isUpdatingChannel, error: updateChannelErrorApi } = useUpdateSubscriptionChannel(queryClient);

  const { mutate: unlinkChannel, isPending: isUnlinkingChannel, error: unlinkChannelErrorApi } = useUnlinkSubscriptionChannel(queryClient);

  // Resetear el estado cuando la suscripción cambie o vuelva a tener un canal
  useEffect(() => {
    if (activeSubscription?.channel?.id) {
      setHasUnlinkedChannel(false);
    }
  }, [activeSubscription?.channel?.id]);

  const { mutate: toggleAutoRenewal, isPending: isTogglingAutoRenewal, error: toggleAutoRenewalErrorApi } = useMutation({
    mutationFn: updateSubscriptionAutoRenewal,
    onSuccess: async () => {
      toast.success(!activeSubscription.autoRenewal ? t('autoRenewalEnabledSuccess') : t('autoRenewalDisabledSuccess'));
      await handleUpdateAndNotify();
    },
    onError: (error: Error) => {
      console.log({error});
      let errorMessage = '';
      if(isAxiosError(error)) {
        errorMessage = error?.response?.data?.message || error.message || ''
      }
      if (errorMessage.includes('No default payment method') || errorMessage.includes('default card')) {
        setShowNoDefaultCardModal(true);
      } else {
        toast.error(`${t('error')}: ${errorMessage || t('failedToUpdateProfile')}`);
      }
    }
  })
  // const { mutate: renewSub, isLoading: isRenewingSub, error: renewSubErrorApi } = useRenewSubscription(queryClient); // Removed

  const handleUpdateAndNotify = async () => {
    await queryClient.refetchQueries({ queryKey: ['userSubscriptions'] });
    await queryClient.refetchQueries({ queryKey: ['userChannels'] });
    if (onSubscriptionUpdate) {
      onSubscriptionUpdate();
    }
  };

  const handleChangeChannelSuccess = async () => {
    toast.success("Channel updated successfully!");
    setHasUnlinkedChannel(false); // Resetear al asignar nuevo canal
    await handleUpdateAndNotify();
    setIsChangeChannelModalOpen(false); // Close modal
  };

  const handleUnlinkChannel = () => {
    if (!activeSubscription?.channel) {
      toast.error(t('noChannelToUnlink'));
      return;
    }

    if (window.confirm(t('confirmUnlinkChannel'))) {
      unlinkChannel(
        { subscriptionId: activeSubscription.id },
        {
          onSuccess: async () => {
            toast.success(t('channelUnlinkedSuccessfully'));
            setHasUnlinkedChannel(true); // Marcar que se desvinculó
            await handleUpdateAndNotify();
          },
          onError: (error: Error) => {
            toast.error(t('errorUnlinkingChannel', { error: error.message }));
          },
        }
      );
    }
  };

  const handleSuccessfullyEnabledRenewal = async () => {
    toast.success(t('autoRenewalSuccessfullyEnabled'));
    // Ensure subscription data is refetched to show the updated status
    await queryClient.refetchQueries({ queryKey: ['userSubscriptions'] });
    if (onSubscriptionUpdate) {
      onSubscriptionUpdate();
    }
    // Optionally, close the modal if it's not already closed by its own logic
    // setShowNoDefaultCardModal(false); // The modal calls its onClose, which should set this.
  };

  const handleToggleAutoRenewalAction = () => {
    if (activeSubscription) {
      toggleAutoRenewal({ subscriptionId: activeSubscription.id, autoRenewal: !activeSubscription.autoRenewal });
    }
  };

  const handleRenewNowAction = () => {
    if (activeSubscription && planDetailsOfActiveSub) { // Ensure planDetailsOfActiveSub is available
      navigate("/payment", { 
        state: { 
          subscriptionId: activeSubscription.id, 
          amount: planDetailsOfActiveSub.price // Assuming price is on planDetailsOfActiveSub
        } 
      });
    } else {
      // Handle the case where activeSubscription or planDetailsOfActiveSub is not available
      // This might involve showing an error message or logging
      console.error("Active subscription or plan details not available for renewal.");
      toast.error(t('couldNotInitiateRenewal'));
    }
  };

  // If plans data is loading (for icons, etc.), show a loader.
  // The main activeSubscription data is passed via prop.
  if (isLoadingPlans) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        <p className="ml-3 text-xl text-gray-700">{t('loadingPlanDetails')}</p>
      </div>
    );
  }

  if (!activeSubscription) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
             {onBackToList && (
                <button
                    onClick={onBackToList}
                    className="self-start mb-4 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                    &larr; {t('back')}
                </button>
            )}
            <p className="text-xl text-gray-700">{t('noActiveSubscriptionSelected')}</p>
        </div>
      );
  }

  // Derive plan details for the passed activeSubscription
  const planDetailsOfActiveSub = plansData?.find(p => p.id === activeSubscription.plan.id) || activeSubscription.plan;
  const ActivePlanIcon = getPlanIcon(planDetailsOfActiveSub?.icon || null);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {onBackToList && (
            <button
                onClick={onBackToList}
                className="mb-6 inline-flex items-center py-2 px-4 bg-white text-gray-700 rounded-lg shadow hover:bg-gray-100 transition-colors"
            >
                <XCircle className="w-5 h-5 mr-2 text-gray-500" /> {t('backToSubscriptionsList')}
            </button>
          )}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">{t('manageYourSubscription')}</h1>
            <p className="text-center text-gray-500 mb-6">{t('planLabel')}: {planDetailsOfActiveSub?.name || activeSubscription.plan.name}</p>

            <div className="text-center mb-6">
              <ActivePlanIcon className={`w-20 h-20 mx-auto mb-3 text-blue-600`} />
            </div>

            {updateChannelErrorApi && <p className="text-red-500 text-center mb-4">{t('errorUpdatingChannel', { error: (updateChannelErrorApi as Error).message })}</p>}
            {unlinkChannelErrorApi && <p className="text-red-500 text-center mb-4">{t('errorUnlinkingChannel', { error: (unlinkChannelErrorApi as Error).message })}</p>}
            {toggleAutoRenewalErrorApi && <p className="text-red-500 text-center mb-4">{t('errorTogglingAutoRenewal', { error: (toggleAutoRenewalErrorApi as Error).message })}</p>}
            {/* {renewSubErrorApi && <p className="text-red-500 text-center mb-4">Error renewing subscription: {(renewSubErrorApi as Error).message}</p>} Removed */}
            {plansError && <p className="text-red-500 text-center mb-4">{t('errorLoadingPlanDetails', { error: (plansError as Error).message })}</p>}


            <div className="space-y-4 mb-8 border-t border-b py-6">
              {/* Displaying details from activeSubscription prop */}
              <div>
                <p className="text-sm text-gray-500">{t('currentChannel')}</p>
                {activeSubscription.channel ? (
                  <p className="text-lg font-semibold text-gray-800">{activeSubscription.channel.nameChannel || activeSubscription.channel.name}</p>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-orange-600">{t('noChannelAssigned')}</p>
                    <XCircle className="w-5 h-5 text-orange-600" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('subscriptionEndDate')}</p>
                <p className="text-lg font-semibold text-gray-800">{new Date(activeSubscription.endDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('autoRenewal')}</p>
                <p className="text-lg font-semibold text-gray-800 flex items-center">
                  {activeSubscription.autoRenewal ? t('on') : t('off')}
                  {isTogglingAutoRenewal && <RefreshCw className="animate-spin w-4 h-4 ml-2 text-gray-500" />}
                </p>
              </div>
               <div>
                <p className="text-sm text-gray-500">{t('status')}</p>
                <p className="text-lg font-semibold text-gray-800">{activeSubscription.status}</p>
              </div>
            </div>

            <div className="space-y-3">
              {!activeSubscription.channel && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-800 mb-2">
                    <strong>{t('attention')}:</strong> {t('subscriptionWithoutChannel')}
                  </p>
                  <p className="text-xs text-orange-700">
                    {t('assignChannelToActivate')}
                  </p>
                </div>
              )}
              <button
                onClick={() => setIsChangeChannelModalOpen(true)}
                disabled={isUpdatingChannel} //isLoadingChannels is internal to modal now
                className={`w-full flex items-center justify-center py-3 px-6 ${
                  activeSubscription.channel 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg font-medium transition-colors disabled:opacity-50`}
              >
                <Edit3 className="w-5 h-5 mr-2" /> 
                {activeSubscription.channel ? t('changeChannel') : t('assignChannel')} 
                {isUpdatingChannel && <RefreshCw className="animate-spin w-4 h-4 ml-2" />}
              </button>

              {activeSubscription.channel && !hasUnlinkedChannel && (
                <button
                  onClick={handleUnlinkChannel}
                  disabled={isUnlinkingChannel}
                  className="w-full flex items-center justify-center py-3 px-6 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  <Unlink className="w-5 h-5 mr-2" /> 
                  {t('unlinkChannel')} 
                  {isUnlinkingChannel && <RefreshCw className="animate-spin w-4 h-4 ml-2" />}
                </button>
              )}

              {(stripeEnabled || activeSubscription.autoRenewal) && (
                <button
                  onClick={handleToggleAutoRenewalAction}
                  disabled={isTogglingAutoRenewal}
                  className="w-full flex items-center justify-center py-3 px-6 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  {activeSubscription.autoRenewal ? t('disableAutoRenewal') : t('enableAutoRenewal')}
                  {isTogglingAutoRenewal && <RefreshCw className="animate-spin w-4 h-4 ml-2" />}
                </button>
              )}

              {/* Updated condition for "Renew Now" button */}
              {shouldShowRenewButton && stripeEnabled && (
                <button
                  onClick={handleRenewNowAction}
                  // disabled={isRenewingSub} // isRenewingSub is removed
                  className="w-full flex items-center justify-center py-3 px-6 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {t('renewNow')} {/* {isRenewingSub && <RefreshCw className="animate-spin w-4 h-4 ml-2" />} Removed */}
                </button>
              )}

              {/* Stripe is disabled: show the admin-configured manual renewal message instead of a payment flow */}
              {shouldShowRenewButton && !stripeEnabled && manualRenewalMessage && (
                <div className="w-full py-3 px-6 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
                  {manualRenewalMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeSubscription && (
        <ChangeChannelModal
          isOpen={isChangeChannelModalOpen}
          onClose={() => setIsChangeChannelModalOpen(false)}
          activeSubscription={activeSubscription}
          currentPlanName={planDetailsOfActiveSub?.name || activeSubscription.plan.name}
          onChannelUpdateSuccess={handleChangeChannelSuccess}
          // onChannelUpdateError can be handled here if needed, or by the modal's internal 
        />
      )}

      {activeSubscription && (
        <NoDefaultCardModal
          isOpen={showNoDefaultCardModal}
          onClose={() => setShowNoDefaultCardModal(false)}
          subscription={activeSubscription}
          onSuccessfullyEnabledRenewal={() => {
            setShowNoDefaultCardModal(false); // Close the modal
            handleSuccessfullyEnabledRenewal(); // Then call the handler
          }}
        />
      )}
    </>
  );
}
